import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PackageCheck, RefreshCw, ArrowRight, Boxes } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_TONE = {
  pending: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  approved: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  in_transit: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40',
  completed: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  rejected: 'bg-red-500/20 text-red-300 border-red-500/40',
};

/**
 * Chef Portal "Stock Requests" tab. Reuses the cashier-scoped transfer
 * endpoints (PIN token). Shows the branch's transfers and lets the chef mark
 * an INCOMING approved/in-transit transfer as Received → status Completed.
 */
export function ChefStockRequests({ branchId }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [recvQty, setRecvQty] = useState({});  // iter941 — { `${transferId}:${idx}`: qty }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/cashier-stock-transfers');
      setTransfers(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load stock requests');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const onBus = (e) => { if (e?.detail?.type === 'stock_request') load(); };
    window.addEventListener('ws:dashboard', onBus);
    const iv = setInterval(load, 15000);
    return () => { window.removeEventListener('ws:dashboard', onBus); clearInterval(iv); };
  }, [load]);

  const qtyFor = (t, idx, fallback) => {
    const k = `${t.id}:${idx}`;
    return recvQty[k] !== undefined ? recvQty[k] : fallback;
  };

  const markReceived = async (t) => {
    setBusyId(t.id);
    try {
      // iter941 — Partial Receive: send the chef-adjusted received_qty per line
      // so stock-in matches what physically arrived (short deliveries).
      const received_items = (t.items || []).map((it, idx) => ({
        ...it,
        received_qty: parseFloat(qtyFor(t, idx, it.quantity)) || 0,
      }));
      await api.put(`/cashier-stock-transfers/${t.id}/complete`, {
        received_items,
        receiver_notes: 'Received at kitchen',
      });
      toast.success('Marked received — transfer completed & auto-settled');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark received');
    } finally { setBusyId(null); }
  };

  const incoming = transfers.filter((t) => t.to_branch_id === branchId);
  const outgoing = transfers.filter((t) => t.from_branch_id === branchId);
  const list = branchId ? [...incoming, ...outgoing.filter((t) => !incoming.some((i) => i.id === t.id))] : transfers;

  return (
    <div className="p-6" data-testid="chef-stock-requests">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Boxes size={22} className="text-amber-400" />
          <h2 className="text-xl font-bold font-outfit">Stock Requests</h2>
          <Badge variant="outline" className="border-stone-600 text-stone-300">{list.length}</Badge>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="border-stone-600 text-stone-300" data-testid="chef-stock-refresh">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-20">
          <PackageCheck size={72} className="mx-auto text-stone-600 mb-4" />
          <p className="text-lg text-muted-foreground">No stock requests for this branch</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((t) => {
            const isIncoming = t.to_branch_id === branchId;
            const canReceive = isIncoming && ['approved', 'in_transit'].includes(t.status);
            return (
              <div key={t.id} className="rounded-2xl bg-stone-800 border border-stone-700 p-4" data-testid={`chef-transfer-${t.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold">{t.from_branch_name}</span>
                    <ArrowRight size={14} className="text-stone-500" />
                    <span className="font-semibold">{t.to_branch_name}</span>
                  </div>
                  <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_TONE[t.status] || 'border-stone-600'}`}>{(t.status || '').replace('_', ' ')}</Badge>
                </div>
                <p className="text-[11px] text-stone-400 mb-2">{isIncoming ? 'Incoming to your branch' : 'Requested from your branch'}{t.reason ? ` · ${t.reason}` : ''}</p>
                <div className="space-y-1 mb-3">
                  {(t.items || []).map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-stone-900/60 rounded-lg px-2 py-1 gap-2">
                      <span className="truncate flex-1">{it.item_name || 'Item'}</span>
                      {canReceive ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-stone-500 text-[10px]">req {it.quantity}</span>
                          <input
                            type="number" min="0" step="any"
                            value={qtyFor(t, i, it.quantity)}
                            onChange={(e) => setRecvQty((p) => ({ ...p, [`${t.id}:${i}`]: e.target.value }))}
                            className="w-16 bg-stone-800 border border-stone-600 text-amber-300 rounded-md px-1.5 py-0.5 text-xs text-right"
                            data-testid={`chef-recv-qty-${t.id}-${i}`}
                            title="Received quantity"
                          />
                        </div>
                      ) : (
                        <span className="text-amber-300 font-mono shrink-0">×{it.received_qty ?? it.quantity}</span>
                      )}
                    </div>
                  ))}
                </div>
                {canReceive ? (
                  <Button onClick={() => markReceived(t)} disabled={busyId === t.id}
                    className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`chef-mark-received-${t.id}`}>
                    <PackageCheck size={16} className="mr-1" /> {busyId === t.id ? 'Confirming…' : 'Mark Received'}
                  </Button>
                ) : (
                  <p className="text-[11px] text-center text-stone-500 py-1">
                    {t.status === 'completed' ? 'Received & completed' : t.status === 'pending' ? 'Awaiting approval' : t.status === 'rejected' ? 'Rejected' : '—'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ChefStockRequests;
